import { JSX } from "react";
import { LandscapeIcon, PortraitIcon, SquareIcon } from "@/components/icons/aspects";
import { ChatCircle, Spiral } from "@phosphor-icons/react";

import ExtendedUGCAgentThumbnail from "@/assets/images/presets/cinametic-ads.webp";
import UGCMarketingVideoThumbnail from "@/assets/images/presets/ugc-marketing-ads.webp";

export type VideoMode = "Ads" | "UGC" | "Avatar";

export const AgentSettings = {
  AvailableAgents: {
    Veo3Director: "veo3-director",
    DefaultAgent: "media-director",
    MediaDirector: "media-director",
  },
  MediaDirector: {
    Video: {
      AnimatedAds: {
        Platforms: ["Facebook", "Instagram", "TikTok", "Snapchat", "Google"] as string[],
        AspectRatios: {
          Facebook: ["9:16", "1:1", "16:9"],
          Instagram: ["9:16", "1:1", "3:4"],
          TikTok: ["9:16", "1:1", "16:9"],
          Snapchat: ["9:16"],
          Google: ["9:16", "1:1", "16:9"],
          List: ["9:16", "1:1", "16:9", "4:3", "3:4"],
        } as Record<string, string[]>,
      },
      CinematicVideoAds: {
        AspectRatios: {
          List: ["9:16", "16:9"],
        } as Record<string, string[]>,
      },
      UGCMarketingVideos: {
        Categories: ["Review", "Testimonial", "Unboxing", "Feature Description"],
        AspectRatios: {
          List: ["9:16", "16:9"],
        } as Record<string, string[]>,
      },
      UGCInfluencerVideos: {
        AspectRatios: {
          List: ["9:16"],
        } as Record<string, string[]>,
      },
      Common: {
        AspectRatios: {
          List: ["9:16", "16:9"],
        } as Record<string, string[]>,
      },
      PresetStyleValues: {
        "Cinematic Video Ads": "CinematicVideoAds",
        "UGC Marketing Ads": "UGCMarketingVideos",
        "UGC Influencer Ads": "UGCInfluencerVideos",
      },
      PresetStyles: [
        {
          name: "Cinematic Video Ads",
          thumbnail: ExtendedUGCAgentThumbnail,
          preview: ExtendedUGCAgentThumbnail,
        },
        {
          name: "UGC Marketing Ads",
          thumbnail: UGCMarketingVideoThumbnail,
          preview: UGCMarketingVideoThumbnail,
        },
      ],
    },
    Image: {
      Ads: {
        Platforms: ["Facebook", "Instagram", "TikTok", "Snapchat", "Google"] as string[],
        AspectRatios: {
          Facebook: ["9:16", "3:4", "1:1", "16:9"],
          Instagram: ["9:16", "3:4", "1:1", "16:9"],
          TikTok: ["9:16", "1:1", "16:9"],
          Snapchat: ["9:16"],
          Google: ["9:16", "1:1", "16:9"],
          List: ["2:3", "9:16", "3:4", "1:1", "3:2", "16:9", "4:3"],
        } as Record<string, string[]>,
      },
      Common: {
        AspectRatios: {
          List: ["2:3", "9:16", "3:4", "1:1", "3:2", "16:9", "4:3"],
        } as Record<string, string[]>,
      },
    },
    Common: {
      Platforms: ["Facebook", "Instagram", "TikTok", "Snapchat", "Google"] as string[],
      AspectRatios: {
        UGC: ["9:16"],
        Facebook: ["9:16", "3:4", "1:1", "16:9"],
        Instagram: ["9:16", "3:4", "1:1", "16:9"],
        TikTok: ["9:16", "1:1", "16:9"],
        Snapchat: ["9:16"],
        Google: ["9:16", "1:1", "16:9"],
        List: ["2:3", "9:16", "3:4", "1:1", "3:2", "16:9", "4:3"],
      } as Record<string, string[]>,
    },
    Quality: [
      {
        value: "basic",
        label: "Basic",
        description: "Cheapest and fastest generation speed",
      },
      {
        value: "high",
        label: "High",
        description: "Highest quality but slower generation",
      },
    ],
    Defaults: {
      Quality: "high",
      AgentMode: "pro",
      AspectRatio: "9:16",
      StylePreset: "auto",
      OutputFormat: "image",
      UseGlobalReferences: false,
    },
  },
  Veo3Director: {
    AspectRatios: {
      List: ["9:16", "16:9"],
    } as Record<string, string[]>,
    Duration: [
      {
        label: "8 seconds",
        value: "8 seconds",
      },
    ],
    Models: [
      {
        value: "veo-3.1",
        label: "Veo 3.1",
        description: "Highest quality but slower generation",
      },
      {
        value: "veo-3.1-fast",
        label: "Veo 3.1 Fast",
        description: "Faster generation but lower quality",
      },
    ],
    ReferenceImages: {
      MaxAmount: 3,
      SupportModels: ["veo-3.1"],
      SupportedAspectRatios: ["16:9"],
      FileFormats: ["image/png", "image/jpeg"],
    },
    Defaults: {
      Model: "veo-3.1",
      AspectRatio: "16:9",
    },
  },
  AgentModes: [
    {
      value: "autopilot",
      icon: Spiral,
      label: "Autopilot",
      description: "The agent will autonomously take decisions without requiring your input",
    },
    {
      value: "pro",
      icon: ChatCircle,
      label: "Pro",
      description: "You get control over the agent's decisions and can provide more detailed instructions",
    },
  ] as const,
  Configurations: ["quality", "platform", "negativePrompt", "stylePreset", "outputFormat", "agentMode"] as string[],
  Icons: {
    "16:9": LandscapeIcon,
    "4:3": LandscapeIcon,
    "3:2": LandscapeIcon,
    "1:1": SquareIcon,
    "3:4": PortraitIcon,
    "9:16": PortraitIcon,
    "2:3": PortraitIcon,
  } as Record<string, () => JSX.Element>,
  Labels: {
    aspectRatio: "Aspect Ratio",
    platform: "Platform",
    videoMode: "Video Mode",
    duration: "Duration",
    quality: "Quality",
    agentMode: "Agent Mode",
    stylePreset: "Style Preset",
    outputFormat: "Output Format",
    useGlobalReferences: "Use Global References",
  } as Record<string, string>,
  Context: {
    product: "Product Image",
    reference: "Reference Template",
  } as Record<string, string>,
  Values: {
    Platform: "platform",
    Duration: "duration",
    Quality: "quality",
    "Aspect Ratio": "aspectRatio",
    "Video Mode": "videoMode",
    "Agent Mode": "agentMode",
    "Output Format": "outputFormat",
    "Style Preset": "stylePreset",
    "Use Global References": "useGlobalReferences",
  } as Record<string, string>,
  Defaults: {
    Notify: false,
  },
};
